// ---------------------------------------------------------------------------
// ADS-012 Phase 2 — ad_pending_mutations persistence schema
// ---------------------------------------------------------------------------
// Story ADS-012: "Campaign actions that spend money or publish externally
// require explicit user approval and audit."
//
// This table is the gate-before-route persistence layer the future
// `requireSpendApproval` middleware and provider SDK refusal guard consume.
// The canonical idempotency key is the operation identity from the packet:
// `(tenant_id, target_kind, target_id, change_payload_hash)`. `mutation_id`
// remains as a caller trace key, but cannot substitute for operation identity.
//
// Provider responses are intentionally minimized: no raw provider JSON is
// stored here. The table keeps only provider_response_id for traceback and
// provider_error_code for failure classification; the future CQRS event can
// store the approved pre/post payload with normal event-log retention.
// ---------------------------------------------------------------------------

import {
  check,
  foreignKey,
  index,
  jsonb,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

const tz = { withTimezone: true } as const;

export const adPendingMutations = pgTable(
  "ad_pending_mutations",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    mutationId: text("mutation_id").notNull(),
    mutationKind: text("mutation_kind").notNull(),
    targetKind: text("target_kind").notNull(),
    targetId: text("target_id").notNull(),
    changePayload: jsonb("change_payload").notNull(),
    changePayloadHash: text("change_payload_hash").notNull(),
    state: text("state").notNull().default("pending_approval"),
    requesterId: uuid("requester_id").notNull(),
    approverId: uuid("approver_id"),
    rejectionReason: text("rejection_reason"),
    providerStatus: text("provider_status").notNull().default("not_started"),
    providerResponseId: text("provider_response_id"),
    providerErrorCode: text("provider_error_code"),
    expiresAt: timestamp("expires_at", tz).notNull(),
    decidedAt: timestamp("decided_at", tz),
    appliedAt: timestamp("applied_at", tz),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("ad_pending_mutations_tenant_mutation_uq").on(
      table.tenantId,
      table.mutationId,
    ),
    uniqueIndex("ad_pending_mutations_operation_identity_uq").on(
      table.tenantId,
      table.targetKind,
      table.targetId,
      table.changePayloadHash,
    ),
    // Codex P1 (PR #1868): a composite (tenant_id, id) unique key gives the
    // history table something to reference, so a tenant-scoped FK can prevent
    // cross-tenant audit contamination. Without this, a write bug could insert
    // a history row tagged with tenant B while pointing at a mutation owned
    // by tenant A, silently breaking every tenant-filtered query.
    uniqueIndex("ad_pending_mutations_tenant_id_uq").on(
      table.tenantId,
      table.id,
    ),
    index("ad_pending_mutations_tenant_state_idx").on(
      table.tenantId,
      table.state,
    ),
    index("ad_pending_mutations_expires_idx")
      .on(table.expiresAt)
      .where(sql`${table.state} = 'pending_approval'`),
    check(
      "ad_pending_mutations_state_ck",
      sql`${table.state} IN ('pending_approval', 'approved', 'applied', 'failed_at_provider', 'rejected', 'expired')`,
    ),
    check(
      "ad_pending_mutations_kind_ck",
      sql`${table.mutationKind} IN ('budget_increase', 'budget_decrease', 'campaign_launch', 'campaign_pause', 'campaign_resume', 'campaign_archive', 'creative_publish', 'audience_update')`,
    ),
    check(
      "ad_pending_mutations_target_kind_ck",
      sql`${table.targetKind} IN ('budget_change', 'campaign_status', 'creative_publish', 'audience_update')`,
    ),
    check(
      "ad_pending_mutations_change_payload_hash_ck",
      sql`${table.changePayloadHash} ~ '^[a-f0-9]{64}$'`,
    ),
    check(
      "ad_pending_mutations_change_payload_object_ck",
      sql`jsonb_typeof(${table.changePayload}) = 'object'`,
    ),
    check(
      "ad_pending_mutations_rejection_reason_ck",
      sql`${table.rejectionReason} IS NULL OR ${table.rejectionReason} IN ('self_approval_blocked', 'approver_not_admin', 'mutation_already_decided', 'ttl_expired', 'provider_quota_exceeded', 'policy_violation', 'tenant_paused', 'no_pending_record')`,
    ),
    check(
      "ad_pending_mutations_provider_status_ck",
      sql`${table.providerStatus} IN ('not_started', 'succeeded', 'failed')`,
    ),
    check(
      "ad_pending_mutations_self_approval_blocked_ck",
      sql`${table.approverId} IS NULL OR ${table.approverId} <> ${table.requesterId}`,
    ),
    check(
      "ad_pending_mutations_lifecycle_consistency_ck",
      sql`
        (
          ${table.state} = 'pending_approval'
          AND ${table.approverId} IS NULL
          AND ${table.decidedAt} IS NULL
          AND ${table.rejectionReason} IS NULL
          AND ${table.appliedAt} IS NULL
        ) OR (
          ${table.state} = 'approved'
          AND ${table.approverId} IS NOT NULL
          AND ${table.decidedAt} IS NOT NULL
          AND ${table.rejectionReason} IS NULL
          AND ${table.appliedAt} IS NULL
        ) OR (
          ${table.state} = 'applied'
          AND ${table.approverId} IS NOT NULL
          AND ${table.decidedAt} IS NOT NULL
          AND ${table.rejectionReason} IS NULL
          AND ${table.appliedAt} IS NOT NULL
        ) OR (
          ${table.state} = 'failed_at_provider'
          AND ${table.approverId} IS NOT NULL
          AND ${table.decidedAt} IS NOT NULL
          AND ${table.rejectionReason} IS NULL
          AND ${table.appliedAt} IS NULL
        ) OR (
          ${table.state} = 'rejected'
          AND ${table.approverId} IS NOT NULL
          AND ${table.decidedAt} IS NOT NULL
          AND ${table.rejectionReason} IS NOT NULL
          AND ${table.appliedAt} IS NULL
        ) OR (
          ${table.state} = 'expired'
          AND ${table.approverId} IS NULL
          AND ${table.decidedAt} IS NOT NULL
          AND ${table.rejectionReason} = 'ttl_expired'
          AND ${table.appliedAt} IS NULL
        )
      `,
    ),
    check(
      "ad_pending_mutations_provider_result_consistency_ck",
      sql`
        (
          ${table.state} IN ('pending_approval', 'approved', 'rejected', 'expired')
          AND ${table.providerStatus} = 'not_started'
          AND ${table.providerResponseId} IS NULL
          AND ${table.providerErrorCode} IS NULL
        ) OR (
          ${table.state} = 'applied'
          AND ${table.providerStatus} = 'succeeded'
          AND ${table.providerResponseId} IS NOT NULL
          AND ${table.providerErrorCode} IS NULL
        ) OR (
          ${table.state} = 'failed_at_provider'
          AND ${table.providerStatus} = 'failed'
          AND ${table.providerResponseId} IS NULL
          AND ${table.providerErrorCode} IS NOT NULL
        )
      `,
    ),
  ],
);

export const adPendingMutationHistory = pgTable(
  "ad_pending_mutation_history",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    pendingMutationId: uuid("pending_mutation_id").notNull(),
    fromState: text("from_state"),
    toState: text("to_state").notNull(),
    eventType: text("event_type").notNull(),
    actorId: uuid("actor_id"),
    reason: text("reason"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    // Codex P1 (PR #1868): tenant-scoped composite FK. A history row tagged
    // with tenant B referencing a mutation owned by tenant A is now physically
    // unrepresentable — the composite key resolves to the matching parent row
    // only when both columns line up. Replaces the previous single-column FK
    // on `pending_mutation_id`, which could not enforce tenant consistency.
    foreignKey({
      name: "ad_pending_mutation_history_tenant_mutation_fk",
      columns: [table.tenantId, table.pendingMutationId],
      foreignColumns: [adPendingMutations.tenantId, adPendingMutations.id],
    }).onDelete("cascade"),
    index("ad_pending_mutation_history_mutation_idx").on(
      table.tenantId,
      table.pendingMutationId,
      table.createdAt,
    ),
    index("ad_pending_mutation_history_event_idx").on(
      table.tenantId,
      table.eventType,
      table.createdAt,
    ),
    check(
      "ad_pending_mutation_history_from_state_ck",
      sql`${table.fromState} IS NULL OR ${table.fromState} IN ('pending_approval', 'approved', 'applied', 'failed_at_provider', 'rejected', 'expired')`,
    ),
    check(
      "ad_pending_mutation_history_to_state_ck",
      sql`${table.toState} IN ('pending_approval', 'approved', 'applied', 'failed_at_provider', 'rejected', 'expired')`,
    ),
    check(
      "ad_pending_mutation_history_event_type_ck",
      sql`${table.eventType} IN ('ads.mutation.requested@v1', 'ads.mutation.approved@v1', 'ads.mutation.rejected@v1', 'ads.mutation.expired@v1', 'ads.mutation.applied@v1', 'ads.mutation.failed_at_provider@v1')`,
    ),
    check(
      "ad_pending_mutation_history_reason_ck",
      sql`${table.reason} IS NULL OR ${table.reason} IN ('self_approval_blocked', 'approver_not_admin', 'mutation_already_decided', 'ttl_expired', 'provider_quota_exceeded', 'policy_violation', 'tenant_paused', 'no_pending_record')`,
    ),
  ],
);
